import { poolForEmail, SIGN_IN_POOL, type SignInPool } from '@bcecloud/portal-ui';
import { getRuntimeConfig } from '@/lib/runtime-config';

/**
 * Which Clerk instance myevents is currently signed in against.
 *
 * The screen serves two audiences from one build: customers, who authenticate
 * against the customer instance, and BCE staff, who authenticate against the
 * team instance to see what a customer sees. Only one `ClerkProvider` can be
 * live on a page, so the choice is a persisted value the app reads once at
 * startup — the same shape as mybcecloud's `auth-mode`, and for the same
 * reason.
 *
 * What differs here is that the choice is *inferred* from the address typed on
 * the sign-in screen rather than picked from a link. See `poolForEmail`.
 */

const STORAGE_KEY = 'bce.myevents.authPool';

/**
 * Where the address is parked across the reload that swaps instances, so the
 * person types their email once rather than once per door.
 */
const PENDING_EMAIL_KEY = 'bce.myevents.pendingEmail';

/**
 * The pool the app should mount.
 *
 * Defaults to the customer instance: myevents belongs to customers, and staff
 * mode is the deliberate exception that gets switched into.
 *
 * @returns The persisted pool, or `client` when unset or unreadable.
 */
export function getAuthPool(): SignInPool {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === SIGN_IN_POOL.staff
      ? SIGN_IN_POOL.staff
      : SIGN_IN_POOL.client;
  } catch {
    return SIGN_IN_POOL.client;
  }
}

/**
 * Persists the pool without navigating.
 *
 * @param pool - The pool to store.
 */
export function setAuthPool(pool: SignInPool): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, pool);
  } catch {
    // A browser refusing storage still works in the default customer pool.
  }
}

/** Reads and clears the address parked before an instance swap. */
export function takePendingEmail(): string | null {
  try {
    const email = window.localStorage.getItem(PENDING_EMAIL_KEY);
    window.localStorage.removeItem(PENDING_EMAIL_KEY);
    return email;
  } catch {
    return null;
  }
}

/**
 * Switches which instance the app signs into and reloads.
 *
 * The reload is load-bearing, exactly as in mybcecloud: the provider picks its
 * key at mount and two Clerk instances cannot coexist on a page, so a clean
 * document is the only safe way to swap which one is live. The address is
 * carried across so the person is not asked to type it twice.
 *
 * @param pool - The pool to switch to.
 * @param email - The address already typed, resumed after the reload.
 */
export function switchAuthPool(pool: SignInPool, email: string): void {
  setAuthPool(pool);
  try {
    window.localStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch {
    // Losing the address costs one retype, not the sign-in.
  }
  // `assign` to the URL you are already on does not reliably re-execute the
  // document, and this is almost always called *from* /auth/login. Without a
  // real reload the provider keeps the previous instance's key while the rest
  // of the app reads the new pool from storage — the two disagree, and the
  // sign-in form renders with no provider behind it.
  if (window.location.pathname === '/auth/login') {
    window.location.reload();
  } else {
    window.location.assign('/auth/login');
  }
}

/**
 * The publishable key for a pool, or an empty string when that instance is not
 * configured in this environment.
 *
 * @param pool - Which instance.
 * @returns The publishable key.
 */
export function publishableKeyFor(pool: SignInPool): string {
  return (
    (pool === SIGN_IN_POOL.staff
      ? getRuntimeConfig('STAFF_CLERK_PUBLISHABLE_KEY')
      : getRuntimeConfig('CLERK_PUBLISHABLE_KEY')) ?? ''
  );
}

/**
 * Whether the pool an address belongs to differs from the one currently
 * mounted — i.e. whether signing this person in needs an instance swap.
 *
 * @param email - The address typed on the sign-in screen.
 * @returns The pool needed, or null when the mounted one already fits.
 */
export function poolSwapNeededFor(email: string): SignInPool | null {
  const wanted = poolForEmail(email);
  return wanted === getAuthPool() ? null : wanted;
}
